'use client';

import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Copy } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { resolveApiErrorToastMessageOrFallback } from '../lib/api/localizeApiError';
import { Select } from './Select';
import { useCreateFeedbackMutation } from '../hooks/queries/useFeedbacks';
import { BaseModal } from './BaseModal';
import { buildDeployDiagnosticsClipboardText } from '../lib/buildInfo';
import { copyTextToClipboard } from '../lib/clipboard';
import { useDeployDiagnostics } from '../hooks/queries/useDeployDiagnostics';

type FeedbackSubmitModalProps = {
  isOpen: boolean;
  token: string | null;
  onCloseAction: () => void;
};

const categoryOptions = ['BUG', 'SUGGESTION', 'CONVENTION', 'UX'] as const;

export function FeedbackSubmitModal({ isOpen, token, onCloseAction }: FeedbackSubmitModalProps) {
  const t = useTranslations('feedbacks.submit');
  const tCommon = useTranslations('common');
  const tRoot = useTranslations();
  const mutation = useCreateFeedbackMutation(token);
  const diagnosticsQuery = useDeployDiagnostics({ enabled: isOpen });

  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setCategory('');
      setTitle('');
      setContent('');
    }
  }, [isOpen]);

  const isValid = useMemo(() => {
    return category.trim().length > 0 && title.trim().length > 0 && content.trim().length > 0;
  }, [category, title, content]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!token) {
      toast.error(t('authRequired'));
      return;
    }

    const normalizedCategory = category.trim().toUpperCase();
    if (!categoryOptions.includes(normalizedCategory as (typeof categoryOptions)[number])) {
      toast.error(t('invalidCategory'));
      return;
    }

    try {
      await mutation.mutateAsync({
        category: normalizedCategory,
        title: title.trim(),
        content: content.trim(),
      });
      toast.success(t('success'));
      onCloseAction();
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('failed'));
      if (message) toast.error(message);
    }
  };

  const handleCopyDiagnostics = async () => {
    try {
      const diagnostics = diagnosticsQuery.data ?? (await diagnosticsQuery.refetch()).data;
      if (!diagnostics) {
        toast.error(t('diagnosticsCopyUnavailable'));
        return;
      }

      await copyTextToClipboard(buildDeployDiagnosticsClipboardText(diagnostics));
      toast.success(t('diagnosticsCopied'));
    } catch {
      toast.error(t('diagnosticsCopyFailed'));
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onCloseAction} variant="bottomSheet" size="md">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">{t('title')}</h3>
          <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{t('subtitle')}</p>
        </div>
        <button type="button" aria-label="Close" className="btn btn-ghost btn-circle btn-sm" onClick={onCloseAction}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form className="flex flex-col gap-4" onSubmit={(event) => void handleSubmit(event)}>
        <div className="form-control">
          <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="feedback-category">
            {t('category')} <span className="text-error">*</span>
          </label>
          <Select
            id="feedback-category"
            name="category"
            value={category}
            onChange={setCategory}
            options={[
              { value: '', label: tCommon('actions.select') },
              ...categoryOptions.map((value) => ({ value, label: value })),
            ]}
          />
        </div>

        <div className="form-control">
          <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="feedback-title">
            {tCommon('fields.title')} <span className="text-error">*</span>
          </label>
          <input
            id="feedback-title"
            name="title"
            className="input input-bordered"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={120}
            required
          />
        </div>

        <div className="form-control">
          <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="feedback-content">
            {t('contentLabel')} <span className="text-error">*</span>
          </label>
          <textarea
            id="feedback-content"
            name="content"
            className="textarea textarea-bordered"
            rows={6}
            value={content}
            onChange={(event) => setContent(event.target.value)}
            maxLength={4000}
            required
          />
          <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{t('notice')}</p>
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => void handleCopyDiagnostics()}
            disabled={diagnosticsQuery.isFetching}
          >
            <Copy className="h-4 w-4" aria-hidden="true" />
            {diagnosticsQuery.isFetching ? t('diagnosticsCopying') : t('diagnosticsCopy')}
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onCloseAction}>
              {tCommon('actions.cancel')}
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={!isValid || mutation.isPending}>
              {mutation.isPending ? t('sending') : t('submit')}
            </button>
          </div>
        </div>
      </form>
    </BaseModal>
  );
}
